import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
  Database,
  RefreshCw,
  Filter,
  CheckCircle2,
  XCircle,
  Clock,
  Cpu,
  Calendar,
  ArrowLeft
} from 'lucide-react';

const AutonomyModels = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [models, setModels] = useState([]);
  
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || ''
  });

  const fetchModels = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      
      const res = await api.get(`/admin/autonomy/models?${params.toString()}`);
      setModels(res.data.models || []);
    } catch (error) {
      console.error('Failed to fetch models:', error);
      toast.error('Failed to load models');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchModels();
  }, [fetchModels]);

  const statusConfig = {
    'training': { color: 'bg-blue-100 text-blue-800', icon: Clock },
    'candidate': { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    'champion': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
    'retired': { color: 'bg-gray-100 text-gray-800', icon: XCircle },
    'failed': { color: 'bg-red-100 text-red-800', icon: XCircle }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="autonomy-models-page">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <Link to="/admin/autonomy" className="text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Model Registry</h1>
                <p className="text-sm text-gray-500">AI models registered in the system</p>
              </div>
            </div>
            <Button onClick={fetchModels} variant="outline" size="sm" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium">Filter:</span>
              <select
                value={filters.status}
                onChange={(e) => {
                  setFilters({ status: e.target.value });
                  if (e.target.value) {
                    setSearchParams({ status: e.target.value });
                  } else {
                    setSearchParams({});
                  }
                }}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="filter-status"
              >
                <option value="">All Statuses</option>
                <option value="training">Training</option>
                <option value="candidate">Candidate</option>
                <option value="champion">Champion</option>
                <option value="retired">Retired</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {/* Models */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : models.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12 text-gray-500">
              <Database className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No models found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {models.map((model) => {
              const StatusIcon = statusConfig[model.status]?.icon || Clock;
              return (
                <Card key={model.id} className="hover:shadow-md transition-shadow" data-testid={`model-${model.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-purple-50">
                          <Cpu className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{model.name}</CardTitle>
                          <p className="text-xs text-gray-400">v{model.version}</p>
                        </div>
                      </div>
                      <Badge className={statusConfig[model.status]?.color || 'bg-gray-100'}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {model.status}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Model Info */}
                    <div className="space-y-2 mb-4">
                      {model.family && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Family</span>
                          <span className="font-medium">{model.family}</span>
                        </div>
                      )}
                      {model.feature_set && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-500">Feature Set</span>
                          <span className="font-medium">{model.feature_set}</span>
                        </div>
                      )}
                    </div>

                    {/* Metrics */}
                    {model.metrics && Object.keys(model.metrics).length > 0 && (
                      <div className="bg-gray-50 rounded-lg p-3 mb-4">
                        <p className="text-xs text-gray-500 mb-2 font-medium">Performance Metrics</p>
                        <div className="grid grid-cols-2 gap-2">
                          {Object.entries(model.metrics).slice(0, 4).map(([key, value]) => (
                            <div key={key} className="text-xs">
                              <span className="text-gray-500 capitalize">{key.replace('_', ' ')}: </span>
                              <span className="font-medium">
                                {typeof value === 'number' ? value.toFixed(3) : value}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Dates */}
                    <div className="text-xs text-gray-400 space-y-1">
                      <p className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Created: {model.created_at ? new Date(model.created_at).toLocaleDateString() : '-'}
                      </p>
                      {model.promoted_at && (
                        <p className="flex items-center gap-1 text-green-600">
                          <CheckCircle2 className="w-3 h-3" />
                          Promoted: {new Date(model.promoted_at).toLocaleDateString()}
                        </p>
                      )}
                      {model.retired_at && (
                        <p className="flex items-center gap-1 text-gray-500">
                          <XCircle className="w-3 h-3" />
                          Retired: {new Date(model.retired_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default AutonomyModels;
